import { useEffect, useState } from 'react';

import {
  applyScrollOffsetVariable,
  DETAIL_NAVIGATION_SCROLL_OFFSET_VARIABLE,
  ZERO_SCROLL_OFFSET,
} from '#/components/layout/CategoryNavigation/scrollOffset';
import { useMediaQuery } from '#/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '#/lib/breakpoints';

function resolveDetailNavigationScrollOffset(
  pageNavElement: HTMLElement,
  isDesktopViewport: boolean,
) {
  return isDesktopViewport
    ? ZERO_SCROLL_OFFSET
    : `${pageNavElement.offsetHeight}px`;
}

/**
 * Owns the detail-page navigation bar's contribution to the global
 * `--scroll-offset` stack.
 *
 * The equity detail page has a second sticky layer beneath the category
 * navigation shell: a local section navigator that becomes a horizontal sticky
 * bar on mobile and a left-rail sticky nav on desktop. Jump-link targets need
 * to reserve space for that mobile bar when it is present, but they must not
 * double-count it on desktop where the nav sits alongside the content.
 *
 * This hook hides the moving pieces required to keep that contract true:
 *
 * - it captures the rendered nav element via a callback ref so the logic only
 *   starts once the query-backed page shell has actually mounted
 * - it measures the nav height and recomputes it on resize and element-size
 *   changes so the offset follows the real UI rather than a hard-coded token
 * - it collapses the contribution to `0px` at the shared desktop breakpoint so
 *   the left rail remains sticky without inflating anchor offsets
 * - it publishes the computed value into the shared CSS-variable stack using
 *   the same ownership model as the category navigation shell
 *
 * Keeping that work in a dedicated hook lets `EquityDetail` stay focused on the
 * page composition while still making the sticky-offset behavior testable in
 * isolation.
 */
export function useDetailNavigationScrollOffset() {
  const [pageNavElement, setPageNavElement] = useState<HTMLElement | null>(
    null,
  );
  const [detailNavigationScrollOffset, setDetailNavigationScrollOffset] =
    useState(ZERO_SCROLL_OFFSET);
  const isDesktopViewport =
    useMediaQuery(DESKTOP_BREAKPOINT_QUERY, pageNavElement != null) ?? false;

  useEffect(() => {
    if (typeof window === 'undefined' || !pageNavElement) {
      return;
    }

    const updateOffset = () => {
      setDetailNavigationScrollOffset(
        resolveDetailNavigationScrollOffset(pageNavElement, isDesktopViewport),
      );
    };

    updateOffset();

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(updateOffset)
        : null;

    resizeObserver?.observe(pageNavElement);
    window.addEventListener('resize', updateOffset);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOffset);
    };
  }, [isDesktopViewport, pageNavElement]);

  useEffect(() => {
    if (typeof document === 'undefined' || !pageNavElement) {
      return;
    }

    return applyScrollOffsetVariable({
      root: pageNavElement,
      doc: document,
      variableName: DETAIL_NAVIGATION_SCROLL_OFFSET_VARIABLE,
      value: detailNavigationScrollOffset,
    });
  }, [detailNavigationScrollOffset, pageNavElement]);

  return { setPageNavElement };
}
